import { useEffect, useMemo, useState } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, Dialog, Input, Select, Textarea } from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { createTasksApi } from "../api";
import { formatLabel, toIsoUtc } from "../labels";
import type { Department, Priority, StaffMember, TaskAction, TaskDto, TaskLinkType } from "../types";

export const PRIORITIES: Priority[] = ["low", "normal", "high", "urgent", "critical"];
export const DEPARTMENTS: Department[] = [
  "operations",
  "customer_support",
  "trust_and_safety",
  "finance",
  "creator_operations",
  "marketing",
  "marketplace_health",
  "system_administration",
];

/** Staff list for assignment, loaded once per mount. */
export function useStaff() {
  const client = useApiClient();
  const api = useMemo(() => createTasksApi(client), [client]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .staff()
      .then((r) => !cancelled && setStaff(r.items))
      .catch((err) => !cancelled && setError(apiErrorMessage(err)));
    return () => {
      cancelled = true;
    };
  }, [api]);
  return { staff, error };
}

export function staffLabel(s: StaffMember): string {
  return `${s.name ?? s.user_id.slice(0, 8)} · ${s.roles.map(formatLabel).join(", ")}`;
}

export function StaffSelect({
  value,
  onChange,
  staff,
  label = "Assignee",
  allowUnassigned = true,
  meId,
}: {
  value: string;
  onChange: (id: string) => void;
  staff: StaffMember[];
  label?: string;
  allowUnassigned?: boolean;
  meId?: string | undefined;
}) {
  const sorted = [...staff].sort((a, b) =>
    a.user_id === meId ? -1 : b.user_id === meId ? 1 : 0,
  );
  return (
    <Select
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      options={[
        ...(allowUnassigned ? [{ value: "", label: "Unassigned" }] : [{ value: "", label: "Choose a person…" }]),
        ...sorted.map((s) => ({
          value: s.user_id,
          label: s.user_id === meId ? `Me (${s.name ?? "you"})` : staffLabel(s),
        })),
      ]}
    />
  );
}

export function CreateTaskDialog({
  open,
  onClose,
  onSubmit,
  busy,
  error,
  meId,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => void | Promise<void>;
  busy: boolean;
  error: string | null;
  meId?: string | undefined;
}) {
  const { staff, error: staffError } = useStaff();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [department, setDepartment] = useState<Department>("operations");
  const [priority, setPriority] = useState<Priority>("normal");
  const [owner, setOwner] = useState("");
  const [due, setDue] = useState("");

  useEffect(() => {
    if (open) {
      setTitle("");
      setDescription("");
      setDepartment("operations");
      setPriority("normal");
      setOwner("");
      setDue("");
    }
  }, [open]);

  const canSubmit = title.trim().length > 0 && !busy;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New task"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={() =>
              void onSubmit({
                title: title.trim(),
                ...(description.trim() ? { description: description.trim() } : {}),
                owning_department: department,
                priority,
                ...(owner ? { owner_user_id: owner } : {}),
                ...(due ? { due_at: toIsoUtc(due) } : {}),
              })
            }
          >
            {busy ? "Creating…" : "Create task"}
          </Button>
        </>
      }
    >
      <Stack gap={12}>
        <Input
          label="What needs doing"
          value={title}
          maxLength={200}
          placeholder="e.g. Call the venue about the double booking"
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
        />
        <Textarea
          label="Details (optional)"
          value={description}
          maxLength={4000}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Select
          label="Team"
          value={department}
          onChange={(e) => setDepartment(e.target.value as Department)}
          options={DEPARTMENTS.map((d) => ({ value: d, label: formatLabel(d) }))}
        />
        <Select
          label="Priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value as Priority)}
          options={PRIORITIES.map((p) => ({ value: p, label: formatLabel(p) }))}
        />
        <StaffSelect value={owner} onChange={setOwner} staff={staff} meId={meId} />
        <Input
          type="datetime-local"
          label="Due (optional)"
          value={due}
          onChange={(e) => setDue(e.target.value)}
        />
        {staffError ? <Alert tone="warning">Staff list unavailable: {staffError}</Alert> : null}
        {error ? <Alert tone="danger">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function EditTaskDialog({
  open,
  task,
  onClose,
  onSubmit,
  busy,
  error,
}: {
  open: boolean;
  task: TaskDto;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => void | Promise<void>;
  busy: boolean;
  error: string | null;
}) {
  const [title, setTitle] = useState(task.title ?? "");
  const [description, setDescription] = useState(task.description ?? "");
  const [department, setDepartment] = useState<Department>(task.owning_department);
  const [priority, setPriority] = useState<Priority>(task.priority);
  const [due, setDue] = useState(task.due_at ? task.due_at.slice(0, 16) : "");

  useEffect(() => {
    if (open) {
      setTitle(task.title ?? "");
      setDescription(task.description ?? "");
      setDepartment(task.owning_department);
      setPriority(task.priority);
      setDue(task.due_at ? toLocalInput(task.due_at) : "");
    }
  }, [open, task]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Edit task"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            disabled={busy || !title.trim()}
            onClick={() =>
              void onSubmit({
                title: title.trim(),
                description: description.trim() ? description.trim() : null,
                owning_department: department,
                priority,
                due_at: due ? toIsoUtc(due) : null,
              })
            }
          >
            {busy ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <Stack gap={12}>
        <Input label="Title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        <Textarea
          label="Details"
          value={description}
          maxLength={4000}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Select
          label="Team"
          value={department}
          onChange={(e) => setDepartment(e.target.value as Department)}
          options={DEPARTMENTS.map((d) => ({ value: d, label: formatLabel(d) }))}
        />
        <Select
          label="Priority"
          value={priority}
          onChange={(e) => setPriority(e.target.value as Priority)}
          options={PRIORITIES.map((p) => ({ value: p, label: formatLabel(p) }))}
        />
        <Input type="datetime-local" label="Due" value={due} onChange={(e) => setDue(e.target.value)} />
        {error ? <Alert tone="danger">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const ACTION_COPY: Partial<
  Record<TaskAction, { title: string; field?: "reason" | "outcome"; required?: boolean; hint: string; danger?: boolean }>
> = {
  block: { title: "Mark blocked", field: "reason", required: true, hint: "What is it waiting on?" },
  cancel: { title: "Cancel task", field: "reason", required: true, hint: "Why is this no longer needed?", danger: true },
  complete: { title: "Complete task", field: "outcome", required: true, hint: "What was done? This is what the next person reads." },
  reopen: { title: "Reopen task", field: "reason", required: true, hint: "Why does this need doing again?" },
  mark_pending_approval: { title: "Waiting on an approval", field: "reason", required: false, hint: "Which approval, if you know it." },
};

/** One dialog for every action that needs a note, or a person. */
export function TaskActionDialog({
  open,
  action,
  onClose,
  onSubmit,
  busy,
  error,
  meId,
  count = 1,
}: {
  open: boolean;
  action: TaskAction | null;
  onClose: () => void;
  onSubmit: (body: { owner_user_id?: string; reason?: string; outcome?: string }) => void | Promise<void>;
  busy: boolean;
  error: string | null;
  meId?: string | undefined;
  count?: number;
}) {
  const { staff } = useStaff();
  const [owner, setOwner] = useState("");
  const [text, setText] = useState("");
  useEffect(() => {
    if (open) {
      setOwner(meId ?? "");
      setText("");
    }
  }, [open, meId]);
  if (!action) return null;
  const copy = ACTION_COPY[action];
  const isAssign = action === "assign";
  const plural = count > 1 ? ` (${count} tasks)` : "";
  const ready = isAssign ? !!owner : !copy?.required || text.trim().length > 0;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={(isAssign ? "Assign" : (copy?.title ?? formatLabel(action))) + plural}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Back
          </Button>
          <Button
            variant={copy?.danger ? "danger" : "primary"}
            disabled={busy || !ready}
            onClick={() =>
              void onSubmit(
                isAssign
                  ? { owner_user_id: owner }
                  : copy?.field === "outcome"
                    ? { outcome: text.trim() }
                    : text.trim()
                      ? { reason: text.trim() }
                      : {},
              )
            }
          >
            {busy ? "Working…" : "Confirm"}
          </Button>
        </>
      }
    >
      <Stack gap={12}>
        {isAssign ? (
          <StaffSelect value={owner} onChange={setOwner} staff={staff} allowUnassigned={false} meId={meId} label="Assign to" />
        ) : (
          <Textarea
            label={copy?.field === "outcome" ? "Outcome" : copy?.required ? "Reason" : "Note (optional)"}
            hint={copy?.hint}
            value={text}
            maxLength={2000}
            onChange={(e) => setText(e.target.value)}
            autoFocus
          />
        )}
        {error ? <Alert tone="danger">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

const LINKABLE: { value: string; label: string }[] = [
  { value: "support_ticket", label: "Support ticket" },
  { value: "booking", label: "Booking" },
  { value: "business", label: "Business" },
  { value: "user", label: "User" },
  { value: "refund", label: "Refund" },
  { value: "payout", label: "Payout" },
  { value: "report", label: "Report" },
  { value: "moderation_case", label: "Moderation case" },
  { value: "community", label: "Community" },
  { value: "creator_application", label: "Creator application" },
  { value: "knowledge_base_item", label: "Knowledge article" },
  { value: "approval", label: "Approval" },
];

export function AddTaskLinkDialog({
  open,
  onClose,
  onSubmit,
  busy,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: { entity_type: string; entity_id: string; link_type: TaskLinkType }) => void | Promise<void>;
  busy: boolean;
  error: string | null;
}) {
  const [entityType, setEntityType] = useState("support_ticket");
  const [entityId, setEntityId] = useState("");
  const [linkType, setLinkType] = useState<TaskLinkType>("related");
  const validId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(entityId.trim());
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Link a record"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            disabled={busy || !validId}
            onClick={() => void onSubmit({ entity_type: entityType, entity_id: entityId.trim(), link_type: linkType })}
          >
            {busy ? "Linking…" : "Link"}
          </Button>
        </>
      }
    >
      <Stack gap={12}>
        <Select label="Record type" value={entityType} onChange={(e) => setEntityType(e.target.value)} options={LINKABLE} />
        <Input
          label="Record ID"
          hint="Copy it from the record's page in HQ."
          value={entityId}
          onChange={(e) => setEntityId(e.target.value)}
          error={entityId && !validId ? "Not a valid ID" : undefined}
        />
        <Select
          label="Relationship"
          value={linkType}
          onChange={(e) => setLinkType(e.target.value as TaskLinkType)}
          options={(["related", "blocker", "dependency", "outcome"] as TaskLinkType[]).map((v) => ({
            value: v,
            label: formatLabel(v),
          }))}
        />
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <Text role="small" color="secondary" as="p">
          A task opened automatically is already linked to the record that caused it.
        </Text>
      </Stack>
    </Dialog>
  );
}
